import { CompositeTilemap } from '@pixi/tilemap';
import type TiledMap from 'tiled-tmj-typedefs/types/TiledMap';
import { Sprite, Texture, Assets, Container, RenderTexture } from 'pixi.js';
import { Spine } from 'pixi-spine';
import type { Object } from 'tiled-tmj-typedefs/types/TiledMap';
import { baseName } from '.';
import type { TNullable } from '~/types/common';
import { ANIMATED_ASSETS } from '~/utils/constants';
import type { Viewport } from 'pixi-viewport';
import type { InteractiveObject } from '~/types/tiledScene';
import { type ISpineRenderTextureManager } from '~/utils/SpineRenderTextureManager';

const FLIPPED_HORIZONTALLY_FLAG = 0x80000000;
const FLIPPED_VERTICALLY_FLAG = 0x40000000;
const FLIPPED_DIAGONALLY_FLAG = 0x20000000;
const FLIP_FLAGS_MASK = ~(FLIPPED_HORIZONTALLY_FLAG | FLIPPED_VERTICALLY_FLAG | FLIPPED_DIAGONALLY_FLAG);

// Define a function to update spine asset animations based on weather
export function updateSpineAnimationsForWeather(container: Container, tilemap: TiledMap, weather: number) {
  // Iterate through each layers container within the main container
  container.children.forEach((layers) => {
    // Check if the current layers container has children
    if (layers.children) {
      // Iterate through the children within the current layers container
      layers.children.forEach((child): void => {
        // Check if the child is an instance of Spine
        const isSpine = child instanceof Spine;
        if (!isSpine) {
          return;
        }

        const assetName = child.name;
        const isPalm = assetName?.includes('palm');
        const isDock = assetName?.includes('dock');

        if (!isPalm && !isDock) {
          return;
        }

        // Find the corresponding animated asset data based on assetName
        const animatedAsset = ANIMATED_ASSETS.find((aa) => aa.name === assetName);

        if (animatedAsset) {
          // Find the scenario animations for the provided weather
          const scenarioAnimations = animatedAsset.animations.find((animation) => animation.value === String(weather));

          if (scenarioAnimations) {
            // Get the array of animations for the scenario
            const animationsToPlay = scenarioAnimations.animation;

            let currentAnimationIndex = 0;

            const playNextAnimation = () => {
              const animationName = animationsToPlay[currentAnimationIndex];
              const isLastAnimation = currentAnimationIndex === animationsToPlay.length - 1;
              if (child.state.tracks?.[0].loop) return;
              // Set the animation to play in the Spine state
              child.state.setAnimation(0, animationName, isLastAnimation);
              if (animationName && !isLastAnimation) {
                currentAnimationIndex++;
              }
            };

            // Set up an event listener for animation completion
            child.state.addListener({
              complete: playNextAnimation
            });
            playNextAnimation(); // Start playing the animations
          } else {
            console.log(`Weather = ${weather} but animation not found for ${assetName}.`);
          }
        }
      });
    }
  });
}

/**
 * Restarts the animations for a specific Spine asset based on weather conditions.
 * Iterates through layers and children in the container and updates animations
 * for the specified asset name when it matches the provided asset name for update.
 *
 * @param container - The container containing the layers and Spine assets.
 * @param tilemap - The TiledMap object.
 * @param weather - The current weather condition.
 * @param assetNameForUpdate - The asset name to update the animations for.
 */
export function restartAnimation(container: Container, tilemap: TiledMap, weather: number, assetNameForUpdate: string) {
  // Iterate through each layers container within the main container
  container.children.forEach((layers) => {
    // Check if the current layers container has children
    if (layers.children) {
      // Iterate through the children within the current layers container
      layers.children.forEach((child) => {
        // Check if the child is an instance of Spine
        const isSpine = child instanceof Spine;
        if (!isSpine) {
          return;
        }

        const assetName = child.name;
        // Find the corresponding animated asset data based on assetName
        const animatedAsset = ANIMATED_ASSETS.find((aa) => aa.name === assetName);
        // Check if the asset name matches the provided name for update
        if (assetNameForUpdate === assetName) {
          if (animatedAsset) {
            // Find the scenario animations for the provided weather
            const scenarioAnimations = animatedAsset.animations.find(
              (animation) => animation.value === String(weather)
            );
            if (scenarioAnimations) {
              // Get the array of animations for the scenario
              const animationsToPlay = scenarioAnimations.animation;
              let currentAnimationIndex = 0;
              // Function to play the next animation in sequence
              const playNextAnimation = () => {
                const animationName = animationsToPlay[currentAnimationIndex];
                const isLastAnimation = currentAnimationIndex === animationsToPlay.length - 1;
                if (child.state.tracks?.[0].loop) return;
                // Set the animation to play in the Spine state
                child.state.setAnimation(0, animationName, isLastAnimation);
                if (animationName && !isLastAnimation) {
                  currentAnimationIndex++;
                }
              };
              // Set up an event listener for animation completion
              child.state.addListener({
                complete: playNextAnimation
              });
              playNextAnimation(); // Start playing the animations
            } else {
              console.log(`Weather = ${weather} but animation not found for ${assetName}.`);
            }
          } else {
            console.log(`Asset ${assetName} not found.`);
          }
        }
      });
    }
  });
}

function isoToOrto(isoX: number, isoY: number) {
  return {
    x: isoX - isoY,
    y: (isoX + isoY) / 2
  };
}

interface PlaceTileOptions {
  tilemap: TiledMap;
  ortX: number;
  ortY: number;
  tilesetGid: number;
  tileWidth: number;
  tileHeight: number;
  basePath: string;
  pathToTexture: Record<string, Texture>;
  weather: number;
  index: number;
  layerOpacity: number;
  object?: TNullable<Object>;
  spriteCont?: TNullable<Container>;
  spineCont?: TNullable<Container>;
  pixiTilemap?: TNullable<CompositeTilemap>;
  spineRenderTextureManager?: TNullable<ISpineRenderTextureManager>;
}

function placeTile(options: PlaceTileOptions) {
  const {
    tilemap,
    tilesetGid: rawGid,
    tileWidth,
    tileHeight,
    basePath,
    pathToTexture,
    weather,
    index,
    layerOpacity,
    object = null,
    spriteCont = null,
    spineCont = null,
    pixiTilemap = null,
    spineRenderTextureManager = null
  } = options;

  let { ortX, ortY } = options;

  const tilesetGid = rawGid & FLIP_FLAGS_MASK;
  const flippedHorizontally = Boolean(rawGid & FLIPPED_HORIZONTALLY_FLAG);
  const flippedVertically = Boolean(rawGid & FLIPPED_VERTICALLY_FLAG);
  const flippedDiagonally = Boolean(rawGid & FLIPPED_DIAGONALLY_FLAG);

  // Find the tileset that contains the specified GID.
  const tileset = tilemap.tilesets.find((t) => tilesetGid >= t.firstgid && tilesetGid < t.firstgid + t.tilecount);
  if (!tileset) {
    return false;
  }

  // Adjust the Y-coordinate for the tileset's position.
  ortY -= tileset.tileheight;

  // Adjust the coordinates for staggered tilemaps.
  ortX -= tilemap.tilewidth / 2;
  ortY -= tilemap.tileheight / 2;

  // Calculate the column and row of the tile in the tileset.
  const col = (tilesetGid - tileset.firstgid) % tileset.columns;
  const row = Math.floor((tilesetGid - tileset.firstgid) / tileset.columns);

  if (
    tileset.tiles &&
    tileset.tiles.length &&
    !tileset.name.includes('WaterSand') &&
    !tileset.name.includes('Tiles_Water') &&
    !tileset.name.includes('GrassGround') &&
    // !tileset.name.includes('Tileset_Water_rocks') &&
    object
  ) {
    const imageId = tilesetGid - tileset.firstgid;
    const tile = tileset.tiles[imageId];

    if (tile && tile.image) {
      // Adjust the coordinates for staggered tile not tilemap!
      ortY += tileset.tileheight;
      ortY -= tileHeight;

      // Check if the tile is an animated asset.
      const animatedAsset = ANIMATED_ASSETS.find((aa) => {
        const sameImage = aa.name === baseName(tile.image);
        return sameImage;
      });

      if (animatedAsset && !animatedAsset.name.includes('palm')) {
        // Get the asset name and necessary tile properties
        const skel = animatedAsset.name + '.skel';
        const localIndex = index;
        const localName = animatedAsset.name;

        // Find the scenario animations for the provided weather
        const scenarioAnimations = animatedAsset.animations.find((animation) => animation.value === String(weather));

        if (scenarioAnimations) {
          const animationsToPlay = scenarioAnimations?.animation;

          const skelPath =
            animatedAsset.name.includes('witch') || animatedAsset.name.includes('halloween')
              ? '/assets/animations/halloween/' + skel
              : '/assets/animations/' + skel;

          return Assets.load(skelPath).then((assetData) => {
            const spineAsset = new Spine(assetData.spineData);

            const localTileScale =
              animatedAsset.name.includes('Ship') ||
              animatedAsset.name.includes('Boat') ||
              animatedAsset.name.includes('ceramic_workshop') ||
              animatedAsset.name.includes('palm') ||
              animatedAsset.name.includes('witch')
                ? 1
                : tileWidth / assetData.spineData?.width;

            // Set initial position and scale for the Spine asset
            spineAsset.x = ortX;
            spineAsset.y = ortY;

            spineAsset.scale.x = localTileScale;
            spineAsset.scale.y = localTileScale;
            spineAsset.name = localName;
            spineAsset.zIndex = localIndex;
            spineAsset.alpha = layerOpacity;

            // -------------------------------------------------------------
            // highlight the object for debug
            // spineAsset.interactive = true;
            // spineAsset.on('pointerdown', () => {
            //   console.log('pointerdown', object);
            // });
            // -------------------------------------------------------------

            let currentAnimationIndex = 0;

            // Function to play the next animation in the scenario
            const playNextAnimation = () => {
              const animationName = animationsToPlay[currentAnimationIndex];
              const isLastAnimation = currentAnimationIndex === animationsToPlay.length - 1;
              if (spineAsset.state.tracks?.[0]?.loop) return;
              // Set the animation to play in the Spine state
              spineAsset.state.setAnimation(0, animationName, isLastAnimation);
              if (animationName && !isLastAnimation) {
                currentAnimationIndex++;
              }
            };

            // Event listener for animation completion
            spineAsset.state.addListener({
              complete: playNextAnimation
            });

            playNextAnimation(); // Start playing the animations
            spineCont?.addChild(spineAsset); // Add the Spine asset to the container

            if (object?.properties?.find((property) => property.name === 'isHoverable')) {
              return { tileObject: object, spriteElement: spineAsset };
            } else return false;
          });
        } else {
          console.log('Weather = ' + weather + ' but animation not found.');
        }
      } else {
        let texture: RenderTexture | Texture | undefined;

        const buildingKey = object?.properties?.find((p) => p.name === 'buildingKey')?.value;
        if (buildingKey === 'forest' && object) {
          const skelName = 'halloween_forest_idle';
          const skelPath = `/assets/animations/halloween/${skelName}.skel`;

          return Assets.load(skelPath).then((assetData) => {
            const spineAsset = new Spine(assetData.spineData);

            const scaleX = tileWidth / assetData.spineData?.width;
            const scaleY = tileHeight / assetData.spineData?.height;

            spineAsset.scale.x = scaleX;
            spineAsset.scale.y = scaleY;

            const bounds = spineAsset.getBounds();
            spineAsset.x = ortX - bounds.x;
            spineAsset.y = ortY - bounds.y;

            spineAsset.name = skelName;
            spineAsset.zIndex = index;
            spineAsset.alpha = layerOpacity;

            spineAsset.state.setAnimation(0, 'idle', true);

            spriteCont?.addChild(spineAsset);

            if (object?.properties?.find((property) => property.name === 'isHoverable')) {
              return { tileObject: object, spriteElement: spineAsset };
            }
            return false;
          });
        }

        if (spineRenderTextureManager && tile.image.includes('palm')) {
          const keys = tile.image.match(/([^/]+)\.\w+$/);
          const textureKey = keys ? keys[1] : null;

          if (!textureKey) {
            return;
          }

          texture = spineRenderTextureManager.getRenderTexture(textureKey);
        } else {
          texture = Texture.from(basePath + tile.image);
        }

        const sprite = new Sprite(texture);
        sprite.x = ortX;
        sprite.y = ortY;
        sprite.zIndex = index;

        if (flippedHorizontally) {
          sprite.anchor.x = 1;
          sprite.scale.x = -1;
        }
        if (flippedVertically) {
          sprite.anchor.y = 1;
          sprite.scale.y = -1;
        }
        if (flippedDiagonally) {
          sprite.anchor.set(0, 1);
          sprite.rotation = -Math.PI / 2;
          sprite.scale.x *= -1;
        }

        sprite.width = tileWidth;
        sprite.height = tileHeight;

        spriteCont?.addChild(sprite);
        sprite.alpha = layerOpacity;

        // -------------------------------------------------------------
        // highlight the object for debug
        sprite.interactive = true;
        sprite.on('pointerdown', () => {
          console.log('pointerdown', object);
        });
        // -------------------------------------------------------------

        if (object?.properties?.find((property) => property.name === 'isHoverable')) {
          return { tileObject: object, spriteElement: sprite };
        }
      }
    }
  } else {
    // Add the tile to the CompositeTilemap using the texture and coordinates.
    if (pathToTexture[tileset.image]) {
      pixiTilemap?.tile(pathToTexture[tileset.image], ortX, ortY, {
        u: col * tileset.tilewidth,
        v: row * tileset.tileheight,
        tileWidth: tileset.tilewidth,
        tileHeight: tileset.tileheight,
        alpha: layerOpacity
      });
    }
  }

  return false;
}

interface TiledToPixiOptions {
  viewport: Viewport;
  tilemap: TiledMap;
  basePath: string;
  weather: number;
  spineRenderTextureManager?: TNullable<ISpineRenderTextureManager>;
}

export async function tiledToPixi(options: TiledToPixiOptions) {
  const { viewport, tilemap, basePath, weather, spineRenderTextureManager = null } = options;

  // Check if the tilemap is infinite and throw an error if it is.
  if (tilemap.infinite) {
    throw new Error('Infinite maps not supported!');
  }

  // Replace the incorrect base path with the correct one in tilemap.tilesets
  tilemap.tilesets.forEach((tileset) => {
    if (tileset.image) {
      tileset.image = tileset.image.replace(/(.*\/Assets)/, '../../../../../assets/new_scenes/Assets');
    }

    if (tileset.tiles && tileset.tiles.length) {
      tileset.tiles.forEach((tile) => {
        if (tile.image) {
          tile.image = tile.image.replace(/(.*\/Assets)/, '../../../../../assets/new_scenes/Assets');
        }
      });
    }
  });

  // Create a dictionary to store the path to each texture.
  const pathToTexture: Record<string, Texture> = {};

  // Load and store each tileset's image as a texture.
  const tilesetPromises = tilemap.tilesets.map(async (tileset) => {
    if (tileset.image) {
      // Check if the texture is already in the cache.
      const textureId = basePath + tileset.image;
      const texture = Texture.from(textureId);
      pathToTexture[tileset.image] = texture;

      if (texture && !texture.baseTexture.valid) {
        // Load the tileset image using the Assets.load() function (assuming it's an asynchronous operation).
        await Assets.load(basePath + tileset.image);
        // Create a new texture and store it in the cache.
        // Store the texture in the dictionary using the image path as the key.
      }
    }

    // Check if the tileset has additional tiles.
    if (tileset.tiles && tileset.tiles.length) {
      const tilePromises = tileset.tiles
        .filter((tile) => tile.image)
        .map(async (tile) => {
          // Check if the texture is already in the cache.
          const tileTextureId = basePath + tile.image;
          const tileTexture = Texture.from(tileTextureId);

          if (!tileTexture.baseTexture.valid && tile.image) {
            // Load the additional tile image using the Assets.load() function.
            // Assets.load(basePath + tile.image);
          }
        });
      await Promise.all(tilePromises);
    }
  });

  await Promise.all(tilesetPromises);

  const interactiveObjects: InteractiveObject[] = [];

  // Create a container for the layer and add it to the PIXI application's stage.
  const cont = new Container();
  cont.sortableChildren = true;
  viewport.addChild(cont);

  // Create a container for the sprites and add it to the PIXI application's stage.
  const spriteCont = new Container();
  spriteCont.sortableChildren = true;
  viewport.addChild(spriteCont);

  // Iterate through each layer in the tilemap.
  let index = 0;

  for (const layer of tilemap.layers) {
    // Skip layers that are not visible.
    index++;
    if (!layer.visible) {
      continue;
    }

    // Define a function to place a tile at the specified coordinates with the given tileset GID.

    // Process the layer based on its type.
    if (layer.type === 'tilelayer') {
      // Create a CompositeTilemap for the layer and add it to the container.
      const pixiTilemap = new CompositeTilemap();
      cont.addChild(pixiTilemap);

      // Iterate through each tile in the layer.
      for (let y = 0; y < layer.height; ++y) {
        for (let x = 0; x < layer.width; ++x) {
          const tileIndex = y * layer.width + x;
          const tilesetGid = layer.data[tileIndex];

          // Skip tiles with GID less than or equal to 0 (no tile).
          if (tilesetGid <= 0) {
            continue;
          }

          let ortX, ortY;

          // Adjust the coordinates based on the tilemap's orientation.
          if (tilemap.orientation === 'staggered') {
            ortX = x * tilemap.tilewidth;
            ortY = (y * tilemap.tileheight) / 2;

            if (y % 2 === 1) {
              ortX += tilemap.tilewidth / 2;
            }
          } else if (tilemap.orientation === 'isometric') {
            ortX = ((x - y) * tilemap.tilewidth) / 2;
            ortY = ((y + x) * tilemap.tileheight) / 2;
          } else {
            throw new Error('Unsupported orientation: ' + tilemap.orientation);
          }

          // Adjust the Y-coordinate.
          ortY += tilemap.tileheight;
          // index++;
          // Place the tile at the calculated coordinates.
          const tileObj = await placeTile({
            tilemap,
            ortX,
            ortY,
            tilesetGid,
            tileWidth: tilemap.tilewidth,
            tileHeight: tilemap.tileheight,
            basePath,
            pathToTexture,
            weather,
            index,
            layerOpacity: layer.opacity,
            pixiTilemap
          });

          if (tileObj) interactiveObjects.push(tileObj);
        }
      }
    } else if (layer.type === 'objectgroup') {
      // Sort the objects in the layer based on their Y-coordinate.
      const objects = [...layer.objects].sort((a, b) => a.y - b.y);

      // Iterate through each object in the layer.
      for (const object of objects) {
        if (object.gid) {
          let x, y;

          // Adjust the coordinates based on the tilemap's orientation.
          if (tilemap.orientation === 'staggered') {
            x = object.x;
            y = object.y;
          } else if (tilemap.orientation === 'isometric') {
            ({ x, y } = isoToOrto(object.x, object.y));
          } else {
            throw new Error('Unsupported orientation: ' + tilemap.orientation);
          }

          const width = object.width;
          const height = object.height;
          // index++;
          // Place the tile at the calculated coordinates.
          const tileObj = await placeTile({
            tilemap,
            ortX: x,
            ortY: y,
            tilesetGid: object.gid,
            tileWidth: width,
            tileHeight: height,
            basePath,
            pathToTexture,
            weather,
            index,
            layerOpacity: layer.opacity,
            object,
            spriteCont: spriteCont,
            spineCont: spriteCont,
            spineRenderTextureManager
          });

          if (tileObj) interactiveObjects.push(tileObj);
        }
      }
    }
  }

  return interactiveObjects;
}
